/**
 * <localization-form> - country/currency and language switchers.
 *
 * Slim port of Dawn's assets/localization-form.js. Selecting an item writes its value
 * into the hidden country_code / locale_code input and submits the form Shopify's
 * {% form 'localization' %} rendered; the storefront handles the rest.
 *
 * Dropped from Dawn (Dawn-specific, and `.menu-drawer` / `.section-header` do not exist
 * here - querySelector would return null and throw): the country search filter, the
 * `country-selector-open` drawer class, the sticky-header `preventHide` flag and the
 * `overflow-hidden-mobile` body class.
 */
export default function () {
  if (customElements.get('localization-form')) return;

  class LocalizationForm extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
        button: this.querySelector('button.localization-form__select'),
        panel: this.querySelector('.disclosure__list-wrapper'),
      };

      if (!this.elements.input || !this.elements.button || !this.elements.panel) return;

      this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
      this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
      this.addEventListener('focusout', this.closeSelector.bind(this));

      this.elements.button.addEventListener('click', this.openSelector.bind(this));
      this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
    }

    hidePanel() {
      this.elements.button.setAttribute('aria-expanded', 'false');
      this.elements.panel.setAttribute('hidden', true);
    }

    openSelector() {
      this.elements.button.focus();
      this.elements.panel.toggleAttribute('hidden');
      this.elements.button.setAttribute(
        'aria-expanded',
        (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
      );
    }

    closeSelector(event) {
      if (!this.contains(event.target) || !this.contains(event.relatedTarget)) {
        this.hidePanel();
      }
    }

    onContainerKeyDown(event) {
      const focusableItems = Array.from(this.querySelectorAll('a'));
      const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
      let itemToFocus;

      switch (event.code.toUpperCase()) {
        case 'ARROWUP':
          event.preventDefault();
          itemToFocus =
            focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
          itemToFocus.focus();
          break;
        case 'ARROWDOWN':
          event.preventDefault();
          itemToFocus =
            focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
          itemToFocus.focus();
          break;
      }
    }

    onContainerKeyUp(event) {
      switch (event.code.toUpperCase()) {
        case 'ESCAPE':
          if (this.elements.button.getAttribute('aria-expanded') === 'false') return;
          event.preventDefault();
          this.hidePanel();
          event.stopPropagation();
          this.elements.button.focus();
          break;
        case 'SPACE':
          if (this.elements.button.getAttribute('aria-expanded') === 'true') return;
          event.preventDefault();
          this.openSelector();
          break;
      }
    }

    onItemClick(event) {
      event.preventDefault();
      const form = this.querySelector('form');
      this.elements.input.value = event.currentTarget.dataset.value;
      if (form) form.submit();
    }
  }

  customElements.define('localization-form', LocalizationForm);
}
